import { useTheme } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts';

import { useWindowSize } from '@verantos-dev/shared-hooks';
import {
  CohortCharacteristicsResultType,
  CohortSummaryAttributeResult,
} from '@verantos-dev/types-cohorts';

import { calculateChartDimensions, wrapLabel } from '../helpers';

type PieChartProps = {
  data: CohortSummaryAttributeResult[] | undefined;
  resultType: CohortCharacteristicsResultType;
};

export const PieChart = ({ data, resultType }: PieChartProps) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowSize();

  if (!data) return null;

  const donutSliceColor = [
    theme.palette.tertiary.lightBlue,
    theme.palette.tertiary.darkBlue,
    theme.palette.tertiary.green,
    theme.palette.tertiary.yellow,
    theme.palette.tertiary.orange,
  ];

  const pieChartData = data.map((item, index) => {
    const formattedLabel = wrapLabel(item.attributeName || '', 18);

    return {
      id: index,
      value: item.attributeCount,
      label: formattedLabel,
      percentage: item.attributePercentage,
      color: donutSliceColor[index % donutSliceColor.length],
    };
  });

  const dimensions = calculateChartDimensions(windowWidth);

  return (
    <MuiPieChart
      data-testid={`cohortCharacteristic-graph-pie-chart-${resultType}`}
      margin={{ left: 24, right: 200, top: 24, bottom: 24 }}
      width={dimensions.width}
      height={dimensions.height}
      series={[
        {
          data: pieChartData || [],
          type: 'pie',
          innerRadius: dimensions.innerRadius,
          valueFormatter: (item, context) => {
            return `${pieChartData[context.dataIndex].value.toLocaleString()} (${
              pieChartData[context.dataIndex].percentage || 0
            }%)`;
          },
        },
      ]}
      slotProps={{
        legend: {
          direction: 'column',
          position: { horizontal: 'right', vertical: 'middle' },
          padding: 0,
          markGap: 5,
          itemGap: 16,
          labelStyle: {
            fontSize: 14,
            fontWeight: 400,
            width: 130,
            whiteSpace: 'normal',
          },
        },
        popper: {
          sx: {
            '& .MuiChartsTooltip-labelCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              '& .MuiTypography-root': {
                color: `${theme.palette.text.secondary}  !important`,
              },
            },
            '& .MuiChartsTooltip-valueCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              paddingLeft: '5px !important',
              '& .MuiTypography-root': {
                color: `${theme.palette.text.primary}  !important`,
              },
            },
          },
        },
      }}
    ></MuiPieChart>
  );
};
